import {useEffect} from 'react';
import {useMutation} from '@apollo/client';
import {Form, Input, Switch} from 'antd';
import SubmitButton from 'components/Startup/components/SubmitButton';
import DBModal from 'components/modal/modal';
import {toast} from 'react-toastify';
import {UPSERT_SUBSCRIPTION_PLAN} from '../resolvers';

const normalizePlanKey = name =>
  `${name || ''}`
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toUpperCase();

const SubscriptionPlanModal = ({
  isOpen,
  onClose,
  planData,
  refetch,
  canSubmit,
}) => {
  const [form] = Form.useForm();
  const isEditMode = Boolean(planData);

  const [upsertSubscriptionPlan, {loading}] = useMutation(
    UPSERT_SUBSCRIPTION_PLAN,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isEditMode) {
      form.setFieldsValue({
        name: planData?.name,
        description: planData?.description,
        isVisibleToUsers: Boolean(planData?.isVisibleToUsers),
        isEnabled: Boolean(planData?.isEnabled),
      });
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      isVisibleToUsers: false,
      isEnabled: true,
    });
  }, [isEditMode, isOpen, planData, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = values => {
    upsertSubscriptionPlan({
      variables: {
        input: {
          key: isEditMode ? planData?.key : normalizePlanKey(values.name),
          name: values.name,
          description: values.description,
          isVisibleToUsers: Boolean(values.isVisibleToUsers),
          isEnabled: Boolean(values.isEnabled),
        },
      },
    })
      .then(({data}) => {
        const response = data?.upsertSubscriptionPlan;

        if (response?.__typename === 'Error') {
          toast.error(response.message);
          return;
        }

        toast.success(response?.message || 'Plan saved successfully');
        handleClose();
        if (refetch) {
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message || 'Failed to save plan');
      });
  };

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={handleClose}
      destroyOnClose
      width={640}
      content={
        <div>
          <h1>
            {isEditMode
              ? 'Update Subscription Plan'
              : 'Create Subscription Plan'}
          </h1>
          <Form
            layout="vertical"
            form={form}
            onFinish={handleSubmit}
            onFinishFailed={() =>
              toast.error('Please fill all required fields')
            }>
            <Form.Item
              label="Name"
              name="name"
              rules={[{required: true, message: 'Plan name is required'}]}>
              <Input placeholder="Enter plan name" />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              rules={[
                {required: true, message: 'Plan description is required'},
              ]}>
              <Input.TextArea
                rows={4}
                placeholder="Enter plan description"
                maxLength={500}
              />
            </Form.Item>
            <Form.Item
              label="Visible To Users"
              name="isVisibleToUsers"
              valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Enabled" name="isEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <SubmitButton
                disabled={loading || !canSubmit}
                label={isEditMode ? 'Update' : 'Create'}
              />
            </div>
          </Form>
        </div>
      }
    />
  );
};

export default SubscriptionPlanModal;
